import React, { useState } from 'react'
import * as yup from 'yup'
import { Moment } from 'moment'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    FormHelperText,
    IconButton,
    LinearProgress,
    SelectChangeEvent,
} from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import {
    isRequiredDate,
    requiredArray,
    requiredId,
    requiredString,
} from '../../../../utils/validation'
import { H6 } from '../../../../layouts/TextStyles'
import MultipleSelect from '../../../../components/muiComponents/multipleSelect/MultipleSelect'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import DatePicker from '../../../../components/muiComponents/datePicker/DatePicker'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { groupApiSlice } from '../../../../features/group/groupSlice'
import { courseUserApiSlice } from '../../../../features/course/courseUserSlice'
import {
    CourseUserEnrollDetailState,
    CourseUserState,
} from '../../../../features/types'
import { formatDateToCalendarDate } from '../../../../utils/functions'

type CourseUser =
    | 'id'
    | 'courseId'
    | 'promocodeId'
    | 'coursePriceId'
    | 'courseSubjectIds'
    | 'startDate'
    | 'endDate'
    | 'language'
    | 'groupId'

type EditCourseUserProps = Pick<CourseUserEnrollDetailState, CourseUser>

interface Props {
    editCourse?: EditCourseUserProps
    editMode?: boolean
    studentId: number
}

const styles = {
    error: {
        color: '#d32f2f',
        marginLeft: 2,
    },
}

const AddStudentCourseDialog = ({ editCourse, editMode, studentId }: Props) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)
    // const isPurchasedOrReceived = Boolean(
    //     editCourse?.promocodeId || editCourse?.coursePriceId,
    // )
    const courseUser: CourseUserState = {
        id: editMode ? editCourse.id : null,
        courseId: editMode ? editCourse.courseId : '',
        courseSubjectIds: editMode ? editCourse?.courseSubjectIds : [],
        startDate: editMode ? editCourse.startDate : null,
        endDate: editMode ? editCourse.endDate : null,
        languageEnum: editMode ? editCourse.language : '',
        studentGroupIds: editMode ? editCourse.groupId : [],
        firstName: '',
        lastName: '',
        mobile: '',
    }

    const validationSchema = yup.object().shape({
        courseId: requiredId({ formatMessage }),
        courseSubjectIds: requiredArray({ formatMessage }),
        startDate: isRequiredDate({ formatMessage }),
        endDate: requiredString({ formatMessage }),
        languageEnum: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: courseUser,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editCourse) {
                handleUpdateStudentCourse()
            } else {
                handleAddStudentToCourse()
            }
        },
    })

    const {
        data: courses = [],
        isFetching: isCoursesFetching,
        isLoading,
    } = courseApiSlice.useFetchAllCoursesQuery(undefined, { skip: !open })

    const {
        data: activeCourseSubjects = [],
        isFetching: isFetchingCourseSubjects,
    } = courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(
        Number(formik.values.courseId),
        { skip: !formik.values.courseId || !open },
    )

    const { data: groups = [], isFetching: isFetcingGroups } =
        groupApiSlice.useGetGroupsForStudentQuery(
            {
                subjectIds: formik.values.courseSubjectIds,
                languageEnum: formik.values.languageEnum,
            },
            {
                skip:
                    !formik.values?.courseSubjectIds?.length ||
                    !formik.values.languageEnum ||
                    !open,
            },
        )

    const [addStudentToCourse, { isLoading: isCreating }] =
        courseUserApiSlice.useAddCourseStudentMutation()

    const [updateCourseStudent, { isLoading: isUpdating }] =
        courseUserApiSlice.useUpdateCourseStudentMutation()

    const handleAddStudentToCourse = async () => {
        try {
            await addStudentToCourse({
                userId: studentId,
                body: formik.values,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleUpdateStudentCourse = async () => {
        try {
            await updateCourseStudent(formik.values).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            //formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleCourseChange = (
        event: SelectChangeEvent<typeof formik.values.courseId>,
    ) => {
        const {
            target: { value },
        } = event
        formik.setFieldValue('courseId', value)
        formik.setFieldValue('courseSubjectIds', [])
        formik.setFieldValue('studentGroupIds', [])
    }

    const handleSubjectChange = (
        event: SelectChangeEvent<typeof formik.values.courseSubjectIds>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue(
            'courseSubjectIds',
            typeof value === 'string' ? value.split(',') : value,
        )
        formik.setFieldValue('studentGroupIds', [])
    }

    const handleDateChange = async (key: string, event: Moment) => {
        formik.setFieldValue(
            key,
            formatDateToCalendarDate(event.toDate(), '-', ['yyyy', 'mm', 'd']),
        )
    }

    const handleLanguageChange = (
        event: SelectChangeEvent<typeof formik.values.languageEnum>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('languageEnum', value)
        formik.setFieldValue('studentGroupIds', [])
    }

    const handleGroupChange = (
        event: SelectChangeEvent<typeof formik.values.studentGroupIds>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('studentGroupIds', value)
    }

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: '#f3f4f5' }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addCourse' })}
                </Button>
            )}

            <Dialog
                maxWidth={'sm'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {editMode
                        ? formatMessage({ id: 'editData' })
                        : formatMessage({ id: 'addCourse' })}
                </DialogTitle>

                <DialogContent>
                    {(isCoursesFetching ||
                        isFetchingCourseSubjects ||
                        isFetcingGroups) && <LinearProgress />}
                    {!isLoading && (
                        <form onSubmit={formik.handleSubmit}>
                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'course' })}</H6>

                                <SingleSelect
                                    id="courseId"
                                    name="courseId"
                                    placeholder={formatMessage({
                                        id: 'course',
                                    })}
                                    value={formik.values.courseId}
                                    disabled={editMode}
                                    menuItems={courses}
                                    handleChange={handleCourseChange}
                                    handleBlur={formik.handleBlur}
                                    handleError={Boolean(
                                        formik.errors.courseId,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.courseId &&
                                        formik.errors.courseId}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'subjects' })}</H6>

                                <MultipleSelect
                                    id="courseSubjectIds"
                                    name="courseSubjectIds"
                                    value={formik.values.courseSubjectIds}
                                    handleChange={handleSubjectChange}
                                    handleBlur={formik.handleBlur}
                                    menuItems={activeCourseSubjects}
                                    multiple={true}
                                    placeholder={formatMessage({
                                        id: 'selectSubject',
                                    })}
                                    handleError={Boolean(
                                        formik.errors.courseSubjectIds,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.courseSubjectIds &&
                                        formik.errors.courseSubjectIds}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>
                                    {formatMessage({ id: 'start&endTime' })}
                                </H6>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        mt: 2,
                                    }}
                                >
                                    <Box sx={{ mr: 2 }}>
                                        <DatePicker
                                            id="startDate"
                                            name="startDate"
                                            label={formatMessage({
                                                id: 'startDate',
                                            })}
                                            value={formik.values.startDate}
                                            handleChange={(event) =>
                                                handleDateChange(
                                                    'startDate',
                                                    event,
                                                )
                                            }
                                            handleError={Boolean(
                                                formik.errors.startDate,
                                            )}
                                            handleBlur={formik.handleBlur}
                                        />

                                        <FormHelperText sx={styles.error}>
                                            {formik.submitCount &&
                                            !formik.values.startDate
                                                ? formatMessage({
                                                      id: 'isRequired',
                                                  })
                                                : null}
                                        </FormHelperText>
                                    </Box>

                                    <Box>
                                        <DatePicker
                                            id="endDate"
                                            name="endDate"
                                            label={formatMessage({
                                                id: 'endDate',
                                            })}
                                            value={formik.values.endDate}
                                            handleChange={(event) =>
                                                handleDateChange(
                                                    'endDate',
                                                    event,
                                                )
                                            }
                                            handleError={Boolean(
                                                formik.errors.endDate,
                                            )}
                                            handleBlur={formik.handleBlur}
                                        />

                                        <FormHelperText sx={styles.error}>
                                            {formik.submitCount &&
                                            !formik.values.endDate
                                                ? formatMessage({
                                                      id: 'isRequired',
                                                  })
                                                : null}
                                        </FormHelperText>
                                    </Box>
                                </Box>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'language' })}</H6>

                                <SingleSelect
                                    id="languageEnum"
                                    name="languageEnum"
                                    value={formik.values.languageEnum}
                                    handleChange={handleLanguageChange}
                                    menuItems={[
                                        { id: 'RU', name: 'RU' },
                                        { id: 'KZ', name: 'KZ' },
                                    ]}
                                    placeholder={formatMessage({
                                        id: 'language',
                                    })}
                                    handleBlur={formik.handleBlur}
                                    handleError={Boolean(
                                        formik.errors.languageEnum,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.languageEnum &&
                                        formik.errors.languageEnum}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'group' })}</H6>

                                <MultipleSelect
                                    id="studentGroupIds"
                                    name="studentGroupIds"
                                    multiple={true}
                                    value={formik.values.studentGroupIds}
                                    handleChange={handleGroupChange}
                                    menuItems={[{ id: '', title: '-' }].concat(
                                        groups,
                                    )}
                                    placeholder={formatMessage({
                                        id: 'group',
                                    })}
                                />
                            </Box>
                        </form>
                    )}
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating || isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={
                            isCreating || isUpdating
                        }
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddStudentCourseDialog
